/**
 * WORLD — the ACTIVE map's layout.
 *
 * This module used to *be* the map. It is now the live view onto whichever map
 * the player voted for: `useMap(spec)` swaps the bindings, and every consumer
 * (`ground.js`, `dressing.js`, `world/index.js`) keeps reading them exactly as
 * before.
 *
 * WHY LIVE BINDINGS AND NOT A PARAMETER
 *   `dressing.js` alone reads STREET / ALLEYS / BUILDINGS / SET_PIECES / GATE
 *   from about ninety call sites across 2.2k lines. An ES module export is a
 *   live binding — a reassignment inside this module is visible to every
 *   importer — and all of those reads happen inside functions, at build time,
 *   never at module scope. So swapping the map here reaches all of them with no
 *   churn in the files that draw the level, which is what keeps a map author's
 *   diff confined to one file under `maps/`.
 *
 * The one rule this imposes: DO NOT destructure these at module scope. Inside a
 * function is fine and is what every consumer already does.
 *
 *   import { STREET } from './layout.js';
 *   const { kerb } = STREET;              // WRONG — freezes the market's value
 *   function f() { const { kerb } = STREET; }   // right
 *
 * `useMap()` is called once by `WorldSystem.init()` before anything builds. See
 * `maps/CONTRACT.md`.
 */

import { MAP as MARKET } from './maps/market.js';

/** The whole spec of the map currently loaded. Never null after useMap(). */
export let MAP = MARKET;

export let STREET = MARKET.STREET;
export let ALLEYS = MARKET.ALLEYS;
export let BUILDINGS = MARKET.BUILDINGS;
export let GATE = MARKET.GATE;
export let SET_PIECES = MARKET.SET_PIECES;

/**
 * Every set-piece list `dressing.js` iterates. A map that does not want market
 * stalls simply omits `stalls`; it must not have to write `stalls: []` to avoid
 * a TypeError, so the missing keys are filled in here instead.
 */
const SET_PIECE_KEYS = [
  'stalls',
  'jerseys',
  'sandbagWalls',
  'wrecks',
  'palms',
  'lamps',
  'cables',
  'laundry',
  'hangings',
  'rubble',
  'tyres',
];

/**
 * Make the given spec the active map. Returns the normalised spec — callers
 * should use the return value rather than their own object, because the
 * defaults below are filled in on a shallow copy.
 */
export function useMap(spec) {
  if (!spec) throw new Error('[world] useMap(spec) called with nothing');

  const m = { ...spec };
  m.STREET = spec.STREET ?? MARKET.STREET;
  m.ALLEYS = spec.ALLEYS ?? [];
  m.BUILDINGS = spec.BUILDINGS ?? [];
  // Only read when `build.gate` is on, but `buildGate` destructures it eagerly.
  m.GATE = spec.GATE ?? MARKET.GATE;
  m.SET_PIECES = { ...(spec.SET_PIECES ?? {}) };
  for (const k of SET_PIECE_KEYS) m.SET_PIECES[k] ??= [];
  m.SPAWNS = spec.SPAWNS?.length ? spec.SPAWNS : MARKET.SPAWNS;
  m.build = { gate: true, perimeter: true, dress: true, debris: true, ...(spec.build ?? {}) };
  m.hour ??= 16.5;
  m.yaw ??= 0;
  m.tx ??= 0;
  m.tz ??= 0;

  MAP = m;
  STREET = m.STREET;
  ALLEYS = m.ALLEYS;
  BUILDINGS = m.BUILDINGS;
  GATE = m.GATE;
  SET_PIECES = m.SET_PIECES;
  return m;
}
